import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  fetchAdminOrder,
  fetchDisputes,
  refundOrder,
  releaseOrderPayout,
  resolveDispute
} from '../../services/disputeService';

const Page = styled.div`
  min-width: 0;
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  margin: 0;
  font-family: var(--font-serif);
  color: var(--color-text);
`;

const Subtitle = styled.p`
  margin: 8px 0 0;
  color: var(--color-text-secondary);
`;

const Button = styled.button`
  border: 1px solid var(--color-border);
  background: ${({ $primary }) => ($primary ? 'var(--color-accent)' : 'var(--color-surface)')};
  color: ${({ $primary }) => ($primary ? '#fff' : 'var(--color-text)')};
  border-radius: 6px;
  padding: 10px 14px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const Select = styled.select`
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 10px 12px;
  background: var(--color-surface);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 420px) minmax(0, 1fr);
  align-items: start;
  gap: 24px;

  @media (max-width: 980px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const List = styled.div`
  display: grid;
  align-content: start;
  min-width: 0;
  gap: 12px;
`;

const CardButton = styled.button`
  border: 1px solid ${({ $active }) => ($active ? 'var(--color-accent)' : 'var(--color-border)')};
  background: var(--color-surface);
  border-radius: 8px;
  padding: 16px;
  text-align: left;
  cursor: pointer;
  box-shadow: ${({ $active }) => ($active ? '0 16px 36px rgba(0, 0, 0, 0.08)' : 'none')};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const Detail = styled.section`
  min-width: 0;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  padding: 24px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-self: flex-start;
  flex-shrink: 0;
  align-items: center;
  border-radius: 999px;
  background: ${({ $tone }) => ($tone === 'danger' ? '#fee2e2' : '#eef2ff')};
  color: ${({ $tone }) => ($tone === 'danger' ? '#991b1b' : 'var(--color-accent)')};
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const MetaGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin: 24px 0;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Meta = styled.div`
  background: var(--color-background);
  border-radius: 6px;
  padding: 14px;

  dt {
    color: var(--color-text-secondary);
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  dd {
    margin: 8px 0 0;
    color: var(--color-text);
    font-weight: 700;
    overflow-wrap: anywhere;
  }
`;

const ActionPanel = styled.div`
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 86px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 12px;
  resize: vertical;
  margin: 12px 0;
`;

const EventList = styled.div`
  border: 1px solid var(--color-border);
  border-radius: 8px;
  overflow: hidden;
  margin-top: 24px;
`;

const EventRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  border-top: 1px solid var(--color-border);

  &:first-child {
    border-top: 0;
  }
`;

const Alert = styled.div`
  border: 1px solid ${({ $error }) => ($error ? '#fecaca' : '#bfdbfe')};
  background: ${({ $error }) => ($error ? '#fef2f2' : '#eff6ff')};
  color: ${({ $error }) => ($error ? '#991b1b' : '#1e40af')};
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 16px;
`;

const formatDate = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not set' : date.toLocaleString();
};

const formatGbp = (pence) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format((Number(pence || 0)) / 100);

const methodLabel = {
  shipping: 'Shipping',
  collection: 'Collector collection',
  artist_dropoff: 'Artist drop-off'
};

const Disputes = () => {
  const [statusFilter, setStatusFilter] = useState('disputed');
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [notes, setNotes] = useState('');
  const requestVersion = useRef(0);

  const load = useCallback(async (preferredOrderId) => {
    const version = ++requestVersion.current;
    setLoading(true);
    setLoadingOrder(false);
    setSelected(null);
    setOrders([]);
    setNotes('');
    setError('');
    setMessage('');
    try {
      const next = await fetchDisputes({ status: statusFilter });
      if (version !== requestVersion.current) return;
      setOrders(next);
      const first = next.find((order) => order.id === preferredOrderId) || next[0];
      if (first) {
        const detail = await fetchAdminOrder(first.id);
        if (version === requestVersion.current) setSelected(detail);
      }
    } catch (err) {
      if (version === requestVersion.current) {
        setError(err.message || 'Unable to load disputes.');
      }
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
    return () => { requestVersion.current += 1; };
  }, [load]);

  const selectOrder = async (order) => {
    if (saving) return;
    const version = ++requestVersion.current;
    setLoadingOrder(true);
    setSelected(null);
    setNotes('');
    setError('');
    setMessage('');
    try {
      const detail = await fetchAdminOrder(order.id);
      if (version === requestVersion.current) setSelected(detail);
    } catch (err) {
      if (version === requestVersion.current) {
        setError(err.message || 'Unable to load this order.');
      }
    } finally {
      if (version === requestVersion.current) setLoadingOrder(false);
    }
  };

  const runAction = async (action, success) => {
    if (!selected || saving || loading || loadingOrder) return;
    const version = ++requestVersion.current;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const updated = await action(selected.id);
      if (version !== requestVersion.current) return;
      const matchesFilter = statusFilter === 'all' || updated.status === statusFilter;
      setSelected(matchesFilter ? updated : null);
      setOrders((current) => current
        .map((order) => (order.id === updated.id ? updated : order))
        .filter((order) => statusFilter === 'all' || order.status === statusFilter));
      setMessage(success);
      setNotes('');
    } catch (err) {
      if (version === requestVersion.current) {
        setError(err.message || 'Unable to update dispute.');
      }
    } finally {
      if (version === requestVersion.current) setSaving(false);
    }
  };

  return (
    <Page>
      <Header>
        <div>
          <Title>Disputes</Title>
          <Subtitle>Review held orders, fulfilment evidence and payout/refund actions.</Subtitle>
        </div>
        <Toolbar>
          <Select
            aria-label="Order status"
            value={statusFilter}
            disabled={saving}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="disputed">Disputed</option>
            <option value="all">All orders</option>
            <option value="completed">Completed</option>
            <option value="refunded">Refunded</option>
          </Select>
          <Button onClick={() => load(selected?.id)} disabled={loading || saving}>Refresh</Button>
        </Toolbar>
      </Header>

      {error && <Alert $error role="alert">{error}</Alert>}
      {message && <Alert role="status">{message}</Alert>}

      {loading && <Alert role="status">Loading disputes...</Alert>}
      {!loading && orders.length === 0 && <Alert>No orders match this filter.</Alert>}
      {!loading && orders.length > 0 && (
        <Grid>
          <List>
            {orders.map((order) => (
              <CardButton
                key={order.id}
                $active={selected?.id === order.id}
                aria-pressed={selected?.id === order.id}
                disabled={saving}
                onClick={() => selectOrder(order)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <strong>{order.artworkTitle || order.id}</strong>
                  <Badge $tone={order.status === 'disputed' ? 'danger' : 'neutral'}>{order.status}</Badge>
                </div>
                <div style={{ marginTop: 8, color: 'var(--color-text-secondary)', fontSize: 13 }}>
                  {methodLabel[order.fulfilmentMethod] || order.fulfilmentMethod} · {formatGbp(order.amounts?.collectorTotalPence)}
                </div>
                <div style={{ marginTop: 8, color: 'var(--color-text-secondary)', fontSize: 12 }}>
                  {order.conversationId}
                </div>
              </CardButton>
            ))}
          </List>

          {loadingOrder && <Alert role="status">Loading order...</Alert>}
          {!loadingOrder && !selected && <p>Select an order to review it.</p>}
          {selected && (
            <Detail>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                <div>
                  <Badge $tone={selected.status === 'disputed' ? 'danger' : 'neutral'}>{selected.status}</Badge>
                  <Title as="h2" style={{ marginTop: 12, fontSize: 28 }}>{selected.artworkTitle}</Title>
                  <Subtitle>{selected.id}</Subtitle>
                </div>
                <Badge>{selected.payoutStatus}</Badge>
              </div>

              <MetaGrid>
                <Meta>
                  <dt>Collector paid</dt>
                  <dd>{formatGbp(selected.amounts?.collectorTotalPence)}</dd>
                </Meta>
                <Meta>
                  <dt>Artist proceeds</dt>
                  <dd>{formatGbp(selected.amounts?.artistProceedsPence)}</dd>
                </Meta>
                <Meta>
                  <dt>Platform revenue</dt>
                  <dd>{formatGbp(selected.amounts?.platformRevenuePence)}</dd>
                </Meta>
                <Meta>
                  <dt>Method</dt>
                  <dd>{methodLabel[selected.fulfilmentMethod] || selected.fulfilmentMethod}</dd>
                </Meta>
              </MetaGrid>

              <ActionPanel>
                <strong>Dispute notes</strong>
                <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                  {selected.dispute?.reason || 'No dispute reason recorded'} · {selected.dispute?.notes || 'No notes'}
                </p>
                <Textarea
                  aria-label="Resolution notes"
                  value={notes}
                  disabled={saving}
                  maxLength={2000}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Resolution notes"
                />
                <Toolbar>
                  <Button
                    $primary
                    disabled={saving || selected.status !== 'disputed'}
                    onClick={() => runAction((id) => resolveDispute(id, 'release_payout', notes), 'Dispute resolved and payout released.')}
                  >
                    Release payout
                  </Button>
                  <Button
                    disabled={saving || selected.status === 'refunded'}
                    onClick={() => runAction((id) => refundOrder(id, notes), 'Order refunded.')}
                  >
                    Refund
                  </Button>
                  <Button
                    disabled={saving || selected.status !== 'disputed'}
                    onClick={() => runAction((id) => resolveDispute(id, 'no_action', notes), 'Dispute marked resolved.')}
                  >
                    Resolve only
                  </Button>
                  <Button
                    disabled={saving || !['held', 'blocked'].includes(selected.payoutStatus) || selected.status !== 'completed'}
                    onClick={() => runAction((id) => releaseOrderPayout(id), 'Payout released.')}
                  >
                    Manual release
                  </Button>
                </Toolbar>
              </ActionPanel>

              <MetaGrid>
                <Meta>
                  <dt>Payment intent</dt>
                  <dd>{selected.paymentIntentId || 'Missing'}</dd>
                </Meta>
                <Meta>
                  <dt>Transfer</dt>
                  <dd>{selected.stripeTransferId || 'Not released'}</dd>
                </Meta>
                <Meta>
                  <dt>Refund</dt>
                  <dd>{selected.stripeRefundId || 'None'}</dd>
                </Meta>
                <Meta>
                  <dt>Review ends</dt>
                  <dd>{formatDate(selected.reviewWindowEndsAt)}</dd>
                </Meta>
              </MetaGrid>

              <EventList>
                {(selected.events || []).map((event) => (
                  <EventRow key={event.id}>
                    <strong>{String(event.type || '').replace(/_/g, ' ')}</strong>
                    <span>{formatDate(event.createdAt)}</span>
                  </EventRow>
                ))}
              </EventList>
            </Detail>
          )}
        </Grid>
      )}
    </Page>
  );
};

export default Disputes;
